import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Drawer, LoadingOverlay } from '@mantine/core';
import {
  FreightDetails,
  FreightShipmentStatus,
  FreightTrackingStatusId,
  isShipmentCancelled,
} from '@shipstation/client-common/components/freight-details';
import { emailFreightDocument } from '@shipstation/client-common/api/ltl.api';
import { GetHelpModal, FREIGHT_GET_HELP_REASON_SETS } from '@shipstation/client-common/components/get-help-modal';
import { mapBackendShipmentToLtlShipment } from '@shipstation/client-common/ltl/freight-payload-mappers';
import { emptyArray } from 'ui-components/default-props/empty';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';
import { NotificationService } from 'ui-components/services/notification-service';

import Global from '../../../../../../store/global-selectors';
import { selectLtlTrackingDetailsByShipmentId } from '../../../../../../store/ltl/core/ltl.core.selectors';
import { goToOrderDetails } from '../../../../../../store/orders/order-details/orders.details.actions';
import { DocumentService } from '../../../../../../services/document-service';
import { combinedFreightDocumentsRequest, downloadDocumentRequest, trackShipmentRequest } from '../../../../../../store/ltl/ltl.actions';
import { getFreightGridRequest } from '../../../../../../store/ltl/grid/ltl.grid.actions';
import { NOTE_TYPES } from '../../../../../../services/order-note-service';
import { useModal } from '../../../../../../hooks/use-modal';
import { useUserHasFreightBookRole } from '../../../../../../hooks/use-user-role';
import { useVoidFreight } from '../../../../../../hooks/freight/use-void-freight';
import { ConfirmDialogueModal } from '../../../../../../components/modals/confirm-dialogue-modal';
import { useOpenFreightGetHelpForm } from './use-open-freight-get-help-form';
import { useOpenFreightSierraChat } from './use-open-freight-sierra-chat';
import { useEnableFreightSupport } from '../../../../../../feature-flags/hooks/use-enable-freight-support';
import { useEnableLtlFreight } from '../../../../../../feature-flags/hooks/use-enable-ltl-freight';
import { useEnableSierraFreightHandoff } from '../../../../../../feature-flags/hooks/use-enable-sierra-freight-handoff';
import { useEnableSierraFreightHandoffSkip } from '../../../../../../feature-flags/hooks/use-enable-sierra-freight-handoff-skip';
import { freightActionButtonMessages } from '../../../freight-action-buttons/messages';
import { bookingConfirmationViewMessages } from './booking-confirmation-view-messages';
import { FreightNavigationButtons } from '../../../freight-navigation-buttons/freight-navigation-buttons';
import { ShareFreightModal } from './components/share-freight-modal/share-freight-modal';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { GetHelpDestination, LtlFreightTrackingEvents } from '../../../../tracking-events';
import type { LtlFreightBookingSource } from '../../../../tracking-events';

import type { GetHelpSubmission } from '@shipstation/client-common/components/get-help-modal';
import type { ShipFrom } from '@shipstation/client-common/types/ship-from';
import { getMajorUnitType } from '@shipstation/client-common/utils/weight-utils';
import type { BaseWeightUnit } from '@shipstation/client-common/types/weight';
import type { DimensionUnit } from '@shipstation/client-common/types/dimensions';
import type { FreightShipment } from '@shipstation/client-common/types/freight-responses';
import type { FreightDocumentType, FreightTrackingHistory, OrderNote } from '@shipstation/client-common/components/freight-details';
import type { LtlAddress, LtlBookingConfirmation, LtlDocument } from '@shipstation/client-common/types/ltl';
import type { SellerFreightProvider } from '@shipstation/client-common/types/freight-providers';

import style from './booking-confirmation-view.module.less';

const DEFAULT_COUNTRY_CODE = 'US';
const SHARE_ALL_DOCS = null;
const FIRST_FREIGHT_BOOKING_AMPLITUDE_ID = 'ltl-first-freight-booking-confirmed';

// Notes are stored one column per type, so a type holds at most one note and the pluralized
// note-type labels always resolve singular.
const SINGLE_NOTE_COUNT = 1;

type FulfillmentPlanNote = { type: string; body: string };

/** The fields the two origin sources — the shipment's stamped address and a ship-from record — have in common. */
type OriginAddressSource = {
  name?: string | null;
  company?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  phone?: string | null;
  email?: string | null;
  ianaTimeZone?: string | null;
};

const toLtlAddress = (address: OriginAddressSource, fallbackCompany?: string | null): LtlAddress => ({
  name: address.name ?? '',
  company: address.company ?? fallbackCompany ?? '',
  line1: address.line1 ?? '',
  line2: address.line2 ?? '',
  city: address.city ?? '',
  state: address.state ?? '',
  postalCode: address.postalCode ?? '',
  countryCode: address.countryCode ?? DEFAULT_COUNTRY_CODE,
  phone: address.phone ?? '',
  email: address.email ?? '',
  locationType: '',
  ianaTimeZone: address.ianaTimeZone ?? null,
});

/**
 * Resolves the origin `LtlAddress` for a booked shipment, preferring the address stamped on the shipment at
 * booking time. Only the ship-from lookup was available before the shipment carried an origin, and it reads
 * the warehouse as it stands *today*: for a voided shipment whose ship-from was since edited it showed the new
 * address, and for a deleted one it showed no origin at all while the grid showed the stamped city.
 * Returns null when neither source has an address on file.
 */
const resolveOriginLtlAddress = (shipment: FreightShipment | null, shipFroms: ShipFrom[]): LtlAddress | null => {
  if (!shipment) return null;
  if (shipment.originAddress) {
    return toLtlAddress(shipment.originAddress);
  }

  const shipFrom = shipFroms.find((sf) => String(sf.shipFromId) === String(shipment.shipFromWarehouseId ?? ''));
  const sfAddr = shipFrom?.originAddress;
  if (!shipFrom || !sfAddr) return null;

  return toLtlAddress(sfAddr, shipFrom.name);
};

type BookingConfirmationViewProps = {
  freightShipmentId: string;
  bookingConfirmation: LtlBookingConfirmation | null;
  onClose: () => void;
  source?: LtlFreightBookingSource;
  onNavigatePrevious?: (() => void) | null;
  onNavigateNext?: (() => void) | null;
  isLoading?: boolean;
  isFirstFreightBooking?: boolean;
};

export const BookingConfirmationView = ({
  freightShipmentId,
  bookingConfirmation,
  onClose,
  source,
  onNavigatePrevious,
  onNavigateNext,
  isLoading = false,
  isFirstFreightBooking = false,
}: BookingConfirmationViewProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  // The entities selector returns a runtime FreightShipment; the typed selector is
  // loosely typed, so cast to the canonical backend shape we feed the mapper.
  const rawShipment = useSelector((state) =>
    freightShipmentId ? Global.selectEntitiesFreightShipmentById(state, { freightShipmentId }) : null,
  ) as FreightShipment | null;

  const { orderNumber, salesOrderId } = useSelector((state) => {
    const fpId = rawShipment?.fulfillmentPlanId;
    if (!fpId) return { orderNumber: undefined, salesOrderId: undefined };
    const fp = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId: fpId });
    const fpSalesOrderId = fp?.salesOrdersFulfilledIds?.[0];
    const fpOrderNumber = fpSalesOrderId
      ? (Global.selectEntitiesSalesOrderProperty(state, { salesOrderId: fpSalesOrderId, property: 'orderNumber' }) ?? undefined)
      : undefined;
    return { orderNumber: fpOrderNumber, salesOrderId: fpSalesOrderId };
  }, shallowEqual);

  const orderNotes = useSelector((state) => {
    const fpId = rawShipment?.fulfillmentPlanId;
    if (!fpId) return emptyArray;
    return Global.selectEntitiesFulfillmentPlanProperty(state, { fulfillmentPlanId: fpId, property: 'notes' }) ?? emptyArray;
  });
  const notes = useMemo<OrderNote[]>(
    () =>
      Object.entries(NOTE_TYPES)
        .map(([noteType, noteTypeLabel]) => ({
          label: intl.formatMessage(noteTypeLabel, { noteCount: SINGLE_NOTE_COUNT }),
          body: orderNotes.find((note: FulfillmentPlanNote) => note.type === noteType)?.body ?? '',
        }))
        .filter((note) => note.body !== ''),
    [intl, orderNotes],
  );

  const sellerFreightProviders = useSelector(Global.selectSellerFreightProviders);
  const shipFroms: ShipFrom[] = useSelector(Global.selectSellerShipFroms);
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit) as BaseWeightUnit;
  const dimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) as DimensionUnit;
  const sellerId = useSelector(Global.selectSellerSellerId) as string | null;
  const sellerPhone = useSelector(Global.selectSellerPhoneNumber) as string | null;
  const displayWeightUnit = getMajorUnitType(weightUnit);
  const displayDimensionUnit = dimensionUnit;

  // This drawer (and the FreightDetails/GetHelpModal it renders) can be opened nested inside the
  // legacy Order Details drawer (orders/order-details/drawer.jsx), which sets its own z-index via
  // useMaxZ() and predates Mantine's global portal defaults (app.jsx) — those set no zIndex, so
  // without this explicit wiring the drawer renders underneath the legacy Order Details drawer.
  const zIndex = useMaxZ();
  const { isOpen: isVoidModalOpen, open: openVoidModal, close: closeVoidModal } = useModal();
  const { isOpen: isGetHelpModalOpen, open: openGetHelpModal, close: closeGetHelpModal } = useModal();
  const { isOpen: isShareModalOpen, open: openShareModal, close: closeShareModal } = useModal();
  const [shareDocument, setShareDocument] = useState<LtlDocument | null>(SHARE_ALL_DOCS);
  const isLtlFreightEnabled = useEnableLtlFreight();
  const isFreightSupportFlagEnabled = useEnableFreightSupport();
  const isFreightSupportEnabled = isLtlFreightEnabled && isFreightSupportFlagEnabled;
  const isSierraFreightHandoffEnabled = useEnableSierraFreightHandoff();
  const isSierraFreightHandoffSkipEnabled = useEnableSierraFreightHandoffSkip();
  const voidFreight = useVoidFreight();
  const hasFreightBookRole = useUserHasFreightBookRole();

  const resolvedOrigin = useMemo<LtlAddress | null>(() => {
    return resolveOriginLtlAddress(rawShipment, shipFroms);
  }, [rawShipment, shipFroms]);

  const ltlShipment = useMemo(
    () => (rawShipment ? mapBackendShipmentToLtlShipment(rawShipment, resolvedOrigin) : null),
    [rawShipment, resolvedOrigin],
  );

  const trackingDetailsByShipmentId = useSelector(selectLtlTrackingDetailsByShipmentId);
  const trackingHistory = useMemo<FreightTrackingHistory | null>(() => {
    if (!ltlShipment) return null;
    const detail = trackingDetailsByShipmentId[ltlShipment.freightShipmentId];
    return {
      events: detail?.events ?? [],
      trackingUrl: detail?.trackingUrl ?? null,
    };
  }, [ltlShipment, trackingDetailsByShipmentId]);

  const shipmentDeliveryDate = useMemo(() => {
    if (ltlShipment?.status !== FreightShipmentStatus.Delivered) return null;
    const deliveredEvent = trackingHistory?.events?.find((event) => event.freightTrackingStatusId === FreightTrackingStatusId.Delivered);
    return deliveredEvent?.eventDateTimeUtc ?? ltlShipment.lastTrackingEventUtc ?? null;
  }, [ltlShipment, trackingHistory]);

  const onPrintDocument = useCallback(
    (document: LtlDocument) => {
      if (!ltlShipment) return;
      dispatch(downloadDocumentRequest(ltlShipment.freightShipmentId, document.key, document.documentType));
    },
    [dispatch, ltlShipment],
  );

  const onDownloadDocument = useCallback(
    (document: LtlDocument) => {
      if (!ltlShipment) return;
      const url = `/api/freight/shipment/${ltlShipment.freightShipmentId}/document/${encodeURI(document.key)}?download=1`;
      DocumentService.downloadFile(url);
    },
    [ltlShipment],
  );

  const onPrintAll = useCallback(() => {
    if (!ltlShipment) return;
    dispatch(combinedFreightDocumentsRequest(ltlShipment.freightShipmentId, 'print'));
  }, [dispatch, ltlShipment]);

  const onDownloadAll = useCallback(() => {
    if (!ltlShipment) return;
    dispatch(combinedFreightDocumentsRequest(ltlShipment.freightShipmentId, 'download'));
  }, [dispatch, ltlShipment]);

  const onShareAll = useCallback(() => {
    setShareDocument(SHARE_ALL_DOCS);
    openShareModal();
  }, [openShareModal]);

  const onShareDocument = useCallback(
    (document: LtlDocument) => {
      setShareDocument(document);
      openShareModal();
    },
    [openShareModal],
  );

  const handleShareRecipients = useCallback(
    async (recipientEmails: string[]) => {
      if (!ltlShipment) return;

      if (shareDocument === SHARE_ALL_DOCS) {
        dispatch(combinedFreightDocumentsRequest(ltlShipment.freightShipmentId, 'share', recipientEmails));
        return;
      }

      const documentTypes = [shareDocument.documentType];
      const results = await Promise.allSettled(
        recipientEmails.map((email) => emailFreightDocument(String(ltlShipment.freightShipmentId), email, documentTypes)),
      );
      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount === 0) {
        NotificationService.showGenericSuccessNotification(undefined, intl.formatMessage(bookingConfirmationViewMessages.shareSuccess));
      } else if (failedCount < recipientEmails.length) {
        NotificationService.showGenericFailureNotification(
          undefined,
          intl.formatMessage(bookingConfirmationViewMessages.sharePartialError, { failedCount, totalCount: recipientEmails.length }),
        );
      } else {
        NotificationService.showGenericFailureNotification(undefined, intl.formatMessage(bookingConfirmationViewMessages.shareError));
      }
    },
    [dispatch, intl, ltlShipment, shareDocument],
  );

  const pendingDocumentTypes = useMemo(() => {
    if (!ltlShipment) return emptyArray as FreightDocumentType[];
    const isPodPresent = ltlShipment.documents.some((d) => d.documentType === 'PROOF_OF_DELIVERY');
    return isPodPresent || ltlShipment.status === FreightShipmentStatus.Delivered
      ? (emptyArray as FreightDocumentType[])
      : (['PROOF_OF_DELIVERY'] as FreightDocumentType[]);
  }, [ltlShipment]);

  const trackedShipmentId = ltlShipment?.freightShipmentId;

  // Also invoked by TrackingHistorySection's staleness check, so this must stay referentially
  // stable across entity re-loads — depend on the id, not the mapped shipment.
  const onTrack = useCallback(() => {
    if (!trackedShipmentId) return;
    dispatch(trackShipmentRequest(trackedShipmentId));
  }, [dispatch, trackedShipmentId]);

  const handleOrderLinkClick = useCallback(() => {
    if (!salesOrderId || !rawShipment?.fulfillmentPlanId) return;
    dispatch(
      goToOrderDetails({ salesOrderId, fulfillmentPlanId: rawShipment.fulfillmentPlanId, selectOrderInGrid: false, replaceRoute: false }),
    );
  }, [dispatch, rawShipment, salesOrderId]);

  const handleVoidConfirm = useCallback(async () => {
    closeVoidModal();
    const fulfillmentPlanId = rawShipment?.fulfillmentPlanId;
    if (!fulfillmentPlanId) return;
    const didSucceed = await voidFreight([fulfillmentPlanId]);
    if (didSucceed) {
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_VOID, {
        fulfillment_plan_id: fulfillmentPlanId,
        source,
      });
      dispatch(getFreightGridRequest());
      onClose();
    }
  }, [closeVoidModal, dispatch, onClose, rawShipment, source, voidFreight]);

  const { openForm, isSubmitting: isGetHelpFormSubmitting } = useOpenFreightGetHelpForm();
  const { openSierraChat, isSubmitting: isSierraChatSubmitting } = useOpenFreightSierraChat();
  const isGetHelpSubmitting = isGetHelpFormSubmitting || isSierraChatSubmitting;

  const supportFieldValues = useMemo(() => {
    const sellerFreightProvider = sellerFreightProviders.find(
      (p: SellerFreightProvider) => p.sellerFreightProviderId === ltlShipment?.sellerFreightProviderId,
    );
    return {
      proNumber: ltlShipment?.proNumber ?? null,
      bolNumber: ltlShipment?.bolNumber ?? '',
      carrierScac: ltlShipment?.carrierScac ?? null,
      customerNumber: sellerFreightProvider?.accountNumber ?? null,
      sellerId: sellerId ?? null,
      phone: sellerPhone ?? null,
      shipDate: ltlShipment?.shipDate ?? '',
      shipmentDeliveryDate,
      originCountryCode: resolvedOrigin?.countryCode ?? null,
      destinationCountryCode: ltlShipment?.destinationAddress?.countryCode ?? null,
      isShipmentInsured: ltlShipment?.insurance?.isInsured ?? null,
      insuredValue: ltlShipment?.insurance?.declaredValue ?? null,
    };
  }, [ltlShipment, sellerFreightProviders, sellerId, sellerPhone, shipmentDeliveryDate, resolvedOrigin]);

  const handleGetHelpSubmit = useCallback(
    async (submission: GetHelpSubmission): Promise<boolean> => {
      const destination = isSierraFreightHandoffEnabled ? GetHelpDestination.SierraChat : GetHelpDestination.ZendeskForm;
      const didOpen = isSierraFreightHandoffEnabled
        ? await openSierraChat(submission, supportFieldValues)
        : await openForm(submission, supportFieldValues);
      if (didOpen) {
        Segment.trackEvent(LtlFreightTrackingEvents.GET_HELP_FORM_OPENED, {
          freight_shipment_id: ltlShipment?.freightShipmentId,
          reason_code: submission.reasonCode,
          destination,
        });
        return true;
      }
      return false;
    },
    [ltlShipment, isSierraFreightHandoffEnabled, openForm, openSierraChat, supportFieldValues],
  );

  const handleGetHelpDirect = useCallback(async () => {
    const didOpen = await openSierraChat(null, supportFieldValues);
    if (didOpen) {
      Segment.trackEvent(LtlFreightTrackingEvents.GET_HELP_FORM_OPENED, {
        freight_shipment_id: ltlShipment?.freightShipmentId,
        reason_code: undefined,
        destination: GetHelpDestination.SierraChat,
      });
    }
  }, [ltlShipment, openSierraChat, supportFieldValues]);

  const isVoidableShipment = Boolean(rawShipment?.fulfillmentPlanId) && !(ltlShipment && isShipmentCancelled(ltlShipment));
  const canVoid = hasFreightBookRole && isVoidableShipment;
  const voidDisabledTooltip =
    !hasFreightBookRole && isVoidableShipment ? intl.formatMessage(freightActionButtonMessages.freightBookDisabledTooltip) : undefined;

  let getHelpHandler: (() => void) | (() => Promise<void>) | undefined;
  if (!isFreightSupportEnabled) {
    getHelpHandler = undefined;
  } else if (isSierraFreightHandoffEnabled && isSierraFreightHandoffSkipEnabled) {
    getHelpHandler = handleGetHelpDirect;
  } else {
    getHelpHandler = openGetHelpModal;
  }

  if (!ltlShipment || isLoading) {
    return (
      <>
        <Drawer
          classNames={{ content: style['drawer-content'], header: style['drawer-header'], title: style['drawer-title'] }}
          data-ignore-root-focus
          onClose={onClose}
          opened
          padding={0}
          position='right'
          size='100%'
          title={intl.formatMessage(bookingConfirmationViewMessages.title)}
          zIndex={zIndex}
        >
          <LoadingOverlay visible />
        </Drawer>
        <FreightNavigationButtons onNavigatePrevious={onNavigatePrevious ?? null} onNavigateNext={onNavigateNext ?? null} />
      </>
    );
  }

  return (
    <>
      <FreightDetails
        amplitudeTrackingId={isFirstFreightBooking ? FIRST_FREIGHT_BOOKING_AMPLITUDE_ID : undefined}
        bookingConfirmation={bookingConfirmation}
        displayDimensionUnit={displayDimensionUnit}
        displayWeightUnit={displayWeightUnit}
        isDocumentsLoading={rawShipment?.documents === null}
        isOpen
        notes={notes}
        onClose={onClose}
        onDownloadAll={onDownloadAll}
        onGetHelp={getHelpHandler}
        onDownloadDocument={onDownloadDocument}
        onOrderLinkClick={orderNumber ? handleOrderLinkClick : undefined}
        onPrintAll={onPrintAll}
        onPrintDocument={onPrintDocument}
        onShareAll={onShareAll}
        onShareDocument={onShareDocument}
        onTrack={onTrack}
        pendingDocumentTypes={pendingDocumentTypes}
        onVoid={canVoid ? openVoidModal : undefined}
        voidDisabledTooltip={voidDisabledTooltip}
        orderNumber={orderNumber}
        originAddress={resolvedOrigin}
        shipment={ltlShipment}
        trackingHistory={trackingHistory}
        zIndex={zIndex}
      />
      <FreightNavigationButtons onNavigatePrevious={onNavigatePrevious ?? null} onNavigateNext={onNavigateNext ?? null} />
      {isShareModalOpen && <ShareFreightModal onClose={closeShareModal} onShare={handleShareRecipients} />}
      {isVoidModalOpen && (
        <ConfirmDialogueModal
          body={<FormattedMessage {...freightActionButtonMessages.voidConfirmBody} />}
          buttonMessage={<FormattedMessage {...freightActionButtonMessages.voidConfirmButton} />}
          buttonTheme='danger'
          header={<FormattedMessage {...freightActionButtonMessages.voidConfirmHeader} />}
          onClose={closeVoidModal}
          onConfirm={handleVoidConfirm}
        />
      )}
      {isFreightSupportEnabled && (
        <GetHelpModal
          isSubmitting={isGetHelpSubmitting}
          onClose={closeGetHelpModal}
          onSubmit={handleGetHelpSubmit}
          opened={isGetHelpModalOpen}
          reasonCodes={FREIGHT_GET_HELP_REASON_SETS.freightShipment}
          referenceLabel={intl.formatMessage(bookingConfirmationViewMessages.getHelpReferenceLabel)}
          referenceValue={ltlShipment.proNumber ?? ltlShipment.bolNumber ?? ''}
          zIndex={zIndex + 1}
        />
      )}
    </>
  );
};
